import { Button, InputField, TextField } from '@design-system';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { useForm } from 'react-hook-form';

import { useAuth } from '../../../providers/AuthProvider';

export type EnquiryFormData = {
  name: string;
  email: string;
  message: string;
};

type Props = {
  onSubmit: (values: EnquiryFormData) => Promise<void>;
  loading: boolean;
};

export const EnquiryForm: React.FC<Props> = ({ loading, onSubmit }) => {
  const { t } = useTranslation('enquiries');
  const { user } = useAuth();

  const form = useForm<EnquiryFormData>({
    defaultValues: {
      name: user?.fullName || '',
      email: user?.email || '',
    },
  });

  const handleSubmit = form.handleSubmit(async (value) => {
    await onSubmit(value);
  });

  return (
    <form
      className="flex w-full flex-col gap-y-6"
      onSubmit={(e) => {
        handleSubmit(e);
      }}
    >
      <div className="flex flex-col gap-y-4">
        <InputField
          id="enquiry_name"
          {...form.register('name', {
            required: {
              value: true,
              message: t('form.fields.name.validation'),
            },
          })}
          dataTestid="field-name"
          disabled={loading}
          error={form.formState.errors.name?.message}
          label={t('form.fields.name.label')}
        />
        <InputField
          {...form.register('email', {
            required: {
              value: true,
              message: t('form.fields.email.validation'),
            },
          })}
          dataTestid="field-email"
          disabled={loading}
          error={form.formState.errors.email?.message}
          id="enquiry_email"
          label={t('form.fields.email.label')}
          type="email"
        />
        <TextField
          {...form.register('message')}
          dataTestid="field-message"
          disabled={loading}
          id="enquiry_message"
          label={t('form.fields.message.label')}
          placeholder={t('form.fields.message.placeholder')}
          rows={5}
        />
      </div>

      <Button
        fullWidth
        label={loading ? t('form.submit.labelLoading') : t('form.submit.label')}
        loading={loading}
        type="submit"
        variant="primary"
      />
    </form>
  );
};
